import React, { useState, ChangeEvent, JSX } from 'react';
import {
  TextField,
  InputAdornment,
  Typography,
  IconButton,
  Tooltip,
} from '@mui/material';
import ShowPassword from '@assetron/web-common/icons/password-icons/ShowPassword';
import ViewPasswordActive from '@assetron/web-common/icons/password-icons/ViewPasswordActive';
import ViewPasswordDefault from '@assetron/web-common/icons/password-icons/ViewPasswordDefault';
import { useLocalization } from '@assetron/common/hooks/useLocalization';
import { EMPTY_STRING } from '@assetron/common/constants/constant';
import useAuthAssetronInputFieldStyles from './styles/useAuthAssetronInputFieldStyles';

interface AuthAssetronInputFieldProps {
  type?: 'default' | 'password';
  destructive?: boolean;
  label?: string;
  hint?: string;
  errorMessage?: string | JSX.Element | boolean;
  disabled?: boolean;
  onTextChange?: (e: ChangeEvent<HTMLInputElement>) => void;
  value?: string;
  iconLeading?: JSX.Element;
  iconTrailing?: JSX.Element;
  dataTestId?: string;
}

function AuthAssetronInputField({
  type = 'default',
  destructive = false,
  label = EMPTY_STRING,
  errorMessage = EMPTY_STRING,
  disabled = false,
  onTextChange,
  value = EMPTY_STRING,
  iconTrailing,
  dataTestId,
}: Readonly<AuthAssetronInputFieldProps>) {
  const styles = useAuthAssetronInputFieldStyles(disabled, destructive);
  const [showPassword, setShowPassword] = useState(false);
  const { t } = useLocalization();

  const handleClickShowPassword = () => {
    setShowPassword((prev) => !prev);
  };

  const getInputType = () => {
    if (type === 'password') {
      return showPassword ? 'text' : 'password';
    }
    return type;
  };

  const helperText = destructive ? (
    <Typography variant="body_12_regular" component="p" color="error.400">
      {errorMessage}
    </Typography>
  ) : null;

  const getPasswordIcon = () => {
    if (showPassword) {
      return <ShowPassword />;
    }
    return value ? <ViewPasswordActive /> : <ViewPasswordDefault />;
  };

  const startAdornment = null;

  let endAdornment = null;
  if (type === 'password') {
    endAdornment = (
      <Tooltip
        title={
          showPassword
            ? t('tooltipTexts.hidePassword')
            : t('tooltipTexts.showPassword')
        }
      >
        <IconButton
          aria-label={t('authCommon.togglePasswordVisibility')}
          onClick={handleClickShowPassword}
          edge="end"
          sx={styles.iconButton}
        >
          {getPasswordIcon()}
        </IconButton>
      </Tooltip>
    );
  } else if (iconTrailing) {
    endAdornment = (
      <InputAdornment position="end">{iconTrailing}</InputAdornment>
    );
  }

  return (
    <TextField
      sx={styles.textField}
      fullWidth
      type={getInputType()}
      variant="outlined"
      label={label}
      value={value}
      onChange={onTextChange}
      disabled={disabled}
      error={destructive}
      helperText={helperText}
      autoComplete={type === 'password' ? 'off' : 'on'}
      data-testid={dataTestId}
      InputProps={{
        startAdornment,
        endAdornment,
      }}
    />
  );
}

export default AuthAssetronInputField;
